/* eslint-disable @typescript-eslint/no-unnecessary-type-constraint */

import { Fragment, ReactNode } from 'react';
import { ApolloError } from '@apollo/client';
import { Box, Center, Collapse, Flex } from '@chakra-ui/react';

import Button from 'components/_core/Button';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';

import Container from './components/Container';
import locales from './locales';

interface FlatListProps<T> {
  data: T[];
  EmptyComponent?: ReactNode;
  error?: ApolloError | null;
  fetchMore?: () => void;
  hasNextPage?: boolean;
  isCollapsed?: boolean;
  isFetchingMore?: boolean;
  isLoading?: boolean;
  getKey: (item: T) => string;
  onCollapse?: () => void;
  refetch?: () => void;
  renderItem: (item: T) => ReactNode;
  title?: string;
}

const FlatList = <T extends unknown>({
  data,
  EmptyComponent = null,
  error = null,
  fetchMore = () => {},
  getKey,
  hasNextPage = false,
  isCollapsed = undefined,
  isFetchingMore = false,
  isLoading = false,
  onCollapse = () => {},
  refetch = () => {},
  renderItem,
  title,
}: FlatListProps<T>) => {
  const canCollapse = isCollapsed !== undefined && onCollapse !== undefined;

  const TitleBlock = (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      marginBottom="space-16"
    >
      <Text fontWeight="weight-500" variant="primary">
        {title}
      </Text>

      {canCollapse ? (
        <Button onClick={onCollapse} variant="inline-primary">
          {isCollapsed ? locales.show : locales.hide}
        </Button>
      ) : null}
    </Flex>
  );

  if (isLoading) {
    return (
      <Container>
        {title ? TitleBlock : null}

        <Box padding="space-16">
          <Loader />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        {title ? TitleBlock : null}
        <ErrorCard retry={refetch} />
      </Container>
    );
  }

  if (data.length === 0) {
    return <Container>{EmptyComponent}</Container>;
  }

  return (
    <Container>
      {title ? TitleBlock : null}

      <Collapse
        /**
         * force the container overflow to be "visible"
         * otherwise the dropdown would be cropped
         * @to-do remove when dropdown is moved into a Portal
         */
        animateOpacity
        in={!canCollapse || !isCollapsed}
        style={{ overflow: 'visible' }}
      >
        <SunshineCard.Group>
          {data.map((dataItem) => (
            <Fragment key={getKey(dataItem)}>{renderItem(dataItem)}</Fragment>
          ))}
        </SunshineCard.Group>

        {hasNextPage ? (
          <Center paddingTop="space-32">
            <Button
              icon="arrow-down"
              isLoading={isFetchingMore}
              onClick={fetchMore}
              variant="secondary"
            >
              {locales.fetchMore}
            </Button>
          </Center>
        ) : null}
      </Collapse>
    </Container>
  );
};

export default FlatList;
